import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { logEvent } from 'firebase/analytics';
import { analytics, functionsInstance } from '../../firebase/firebase';
import { httpsCallable } from 'firebase/functions';
import TrialPromptPopup from './TrialPromptPopup';

const May2026 = () => {
  const hasLoggedRead = useRef(false);
  const [email, setEmail] = useState('');
  const [gdprChecked, setGdprChecked] = useState(false);
  const [submitState, setSubmitState] = useState('idle');
  const [errorMsg, setErrorMsg] = useState('');
  const [showTrialPopup, setShowTrialPopup] = useState(false);

  useEffect(() => {
    document.title = "Top TPT Keywords — May 2026 | Mr Joel's Seller Tools";
    window.scrollTo(0, 0);

    logEvent(analytics, 'page_view', {
      page_title: 'May 2026 Keyword Report Signup',
      page_path: '/keywords/may-2026'
    });

    const handleScroll = () => {
      if (window.scrollY > 300 && !hasLoggedRead.current) {
        logEvent(analytics, 'may_2026_read_content');
        hasLoggedRead.current = true;
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const trackFreeTrialClick = () => {
    logEvent(analytics, 'keyword_report_click', { button_name: 'nav_free_trial' });
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (!gdprChecked) return;
    setSubmitState('loading');
    setErrorMsg('');

    try {
      logEvent(analytics, 'may_2026_signup_submit');
      const subscribeToReport = httpsCallable(functionsInstance, 'subscribeToSeasonalReport');
      // Adding the specific may-2026 tag here
      const result = await subscribeToReport({ email, tag: 'may-2026' });
      const { userType } = result.data;

      if (userType === 'existing') {
        setSubmitState('success-existing');
        logEvent(analytics, 'may_2026_existing_user');
      } else {
        setSubmitState('success-new');
        logEvent(analytics, 'may_2026_new_subscriber');
        setTimeout(() => setShowTrialPopup(true), 5000);
      }
    } catch (err) {
      console.error('Signup error:', err);
      setErrorMsg('Something went wrong — please try again or email info@mrjoelstptgenerator.com');
      setSubmitState('error');
    }
  };

  const scrollToEmail = (e) => {
    e.preventDefault();
    const emailInput = document.getElementById('report-email');
    if (emailInput) {
      emailInput.focus();
      emailInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  return (
    <div className="keyword-report-wrapper">

      {showTrialPopup && (
        <TrialPromptPopup
          onClose={() => setShowTrialPopup(false)}
          accentColor="#1a56db"
          accentDark="#1e40af"
          accentLight="#eff6ff"
          accentBorder="#bfdbfe"
        />
      )}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;600;700;800;900&family=DM+Sans:ital,wght@0,400;0,500;1,400&display=swap');

        .keyword-report-wrapper {
          --blue: #1a56db;
          --blue-dark: #1e40af;
          --blue-light: #eff6ff;
          --gold: #fbbf24;
          --gold-bg: #fffbeb;
          --green: #10b981;
          --green-bg: #f0fdf4;
          --text: #0f172a;
          --muted: #64748b;
          --border: #e2e8f0;
          --bg: #f8fafc;
          font-family: 'DM Sans', sans-serif;
          background: var(--bg);
          color: var(--text);
          min-height: 100vh;
          overflow-x: hidden;
        }

        /* ── NAV ── */
        .report-nav {
          display: flex; align-items: center; justify-content: space-between;
          padding: 16px 32px;
          background: white;
          border-bottom: 1px solid var(--border);
          position: sticky; top: 0; z-index: 100;
        }

        .nav-logo { display: flex; align-items: center; gap: 10px; text-decoration: none; }

        .nav-logo-mark {
          width: 36px; height: 36px;
          background: linear-gradient(135deg, var(--blue), #0e9f6e);
          border-radius: 9px;
          display: flex; align-items: center; justify-content: center;
          font-family: 'Sora', sans-serif;
          font-weight: 900; font-size: 14px; color: white;
        }

        .nav-logo-text { font-family: 'Sora', sans-serif; font-weight: 700; font-size: 14px; color: var(--text); }
        .nav-logo-sub { font-size: 11px; color: var(--muted); }

        .nav-cta {
          font-family: 'Sora', sans-serif;
          font-size: 13px; font-weight: 600;
          color: var(--blue); text-decoration: none;
          border: 1px solid #c7d7f9;
          padding: 7px 16px; border-radius: 8px;
          transition: background 0.15s;
        }
        .nav-cta:hover { background: var(--blue-light); }

        /* ── FULL WIDTH HERO ── */
        .hero-full {
          max-width: 1080px;
          margin: 0 auto;
          padding: 60px 24px 40px;
          text-align: center;
        }

        .badge {
          display: inline-flex; align-items: center; gap: 7px;
          background: var(--gold-bg);
          border: 1px solid #fde68a;
          color: #92400e;
          font-size: 12px; font-weight: 700;
          padding: 6px 16px; border-radius: 20px;
          margin-bottom: 24px;
          letter-spacing: 0.03em; text-transform: uppercase;
        }

        .badge-dot {
          width: 6px; height: 6px;
          background: var(--gold); border-radius: 50%;
          animation: pulse 2s infinite;
        }

        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.5; transform: scale(1.4); }
        }

        .keyword-report-wrapper h1, .sora {
          font-family: 'Sora', sans-serif;
        }

        .hero-title {
          font-weight: 900;
          font-size: clamp(32px, 5vw, 56px);
          line-height: 1.1;
          letter-spacing: -0.03em;
          color: var(--text);
          margin-bottom: 20px;
        }

        .hero-title .accent {
          background: linear-gradient(135deg, var(--blue), #0369a1);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .hero-title .gold { color: #d97706; -webkit-text-fill-color: #d97706; }

        .hero-sub {
          font-size: 18px; color: var(--muted);
          line-height: 1.7; margin-bottom: 40px;
          max-width: 800px; margin-left: auto; margin-right: auto;
        }

        .hero-sub strong { color: var(--text); }

        /* ── STATS ── */
        .stats {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 16px;
          max-width: 800px;
          margin: 0 auto;
        }

        .stat {
          background: white;
          border: 1px solid var(--border);
          border-radius: 16px;
          padding: 20px;
          text-align: center;
        }

        .stat-num {
          font-weight: 900; font-size: 28px;
          color: var(--blue); line-height: 1;
          margin-bottom: 4px;
        }

        .stat-label {
          font-size: 12px; color: var(--muted);
          text-transform: uppercase; letter-spacing: 0.05em;
          font-weight: 500; line-height: 1.3;
        }

        /* ── MAIN LAYOUT ── */
        .page-grid {
          max-width: 1080px;
          margin: 0 auto;
          padding: 0 24px 60px;
          display: grid;
          grid-template-columns: 1fr 420px;
          gap: 40px;
          align-items: start;
        }

        /* ── CARDS (Left & Right Balanced) ── */
        .info-card, .form-card {
          background: white;
          border: 1px solid var(--border);
          border-radius: 24px;
          padding: 32px;
          box-shadow: 0 10px 40px rgba(0,0,0,0.06);
        }

        .form-card {
          position: sticky;
          top: 100px;
        }

        .section-label {
          font-size: 11px; font-weight: 700;
          color: var(--blue); text-transform: uppercase;
          letter-spacing: 0.1em; margin-bottom: 12px;
        }

        .card-heading {
          font-weight: 800; font-size: 22px;
          color: var(--text); margin-bottom: 24px;
          line-height: 1.2;
        }

        .perks-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 12px;
        }

        .perk {
          display: flex; align-items: center; gap: 10px;
          background: var(--bg);
          border: 1px solid var(--border);
          border-radius: 12px;
          padding: 14px 16px;
          font-size: 13px; font-weight: 500;
          color: var(--text);
        }

        /* ── FORM ELEMENTS ── */
        .form-sub {
          font-size: 14px; color: var(--muted);
          margin-bottom: 24px; line-height: 1.5;
        }

        .field-label {
          display: block; font-size: 13px; font-weight: 600;
          color: #374151; margin-bottom: 6px;
        }

        .field-input {
          width: 100%; padding: 14px;
          border: 1.5px solid var(--border);
          border-radius: 12px;
          font-size: 15px; color: var(--text);
          font-family: 'DM Sans', sans-serif;
          outline: none; margin-bottom: 20px;
          background: #f8fafc; transition: all 0.2s;
        }

        .field-input:focus { border-color: var(--blue); background: white; }

        .gdpr-box {
          background: #f8fafc;
          border: 1px solid var(--border);
          border-radius: 12px;
          padding: 14px;
          margin-bottom: 20px;
        }

        .gdpr-label { display: flex; gap: 10px; cursor: pointer; }
        .gdpr-checkbox { width: 16px; height: 16px; flex-shrink: 0; margin-top: 3px; accent-color: var(--blue); }
        .gdpr-text { font-size: 12px; color: var(--muted); line-height: 1.5; }
        .gdpr-text a { color: var(--blue); }

        .submit-btn {
          width: 100%;
          background: linear-gradient(135deg, var(--blue), var(--blue-dark));
          color: white; font-weight: 800; font-size: 15px;
          padding: 16px; border: none; border-radius: 12px;
          cursor: pointer; box-shadow: 0 4px 14px rgba(26,86,219,0.3);
          transition: transform 0.15s;
        }
        .submit-btn:hover { transform: translateY(-1px); }

        .amazon-badge {
          background: var(--gold-bg); border: 1px solid #fde68a;
          color: #92400e; font-size: 12px; padding: 12px;
          border-radius: 10px; margin-top: 16px; text-align: center;
        }

        .testimonial {
          margin-top: 24px; background: var(--blue-light);
          border: 1px solid #c7d7f9; border-radius: 12px; padding: 16px;
        }

        /* ── PREVIEW TABLE ── */
        .preview-table-row {
          display: grid; grid-template-columns: 32px 1fr 100px 90px 110px;
          gap: 0; padding: 13px 32px; border-bottom: 1px solid #f1f5f9; align-items: center;
        }

        /* ── RESPONSIVE ── */
        @media (max-width: 850px) {
          .page-grid { grid-template-columns: 1fr; }
          .form-card { position: static; }
          .stats { grid-template-columns: 1fr; }
          .perks-grid { grid-template-columns: 1fr; }
          .hero-title { font-size: 32px; }
        }
      `}</style>

      {/* NAV */}
      <nav className="report-nav">
        <Link to="/" className="nav-logo">
          <div className="nav-logo-mark">MJ</div>
          <div>
            <div className="nav-logo-text">Mr Joel's Seller Tools</div>
            <div className="nav-logo-sub">TPT Intelligence</div>
          </div>
        </Link>
        <Link to="/app" onClick={trackFreeTrialClick} className="nav-cta">Try the App Free →</Link>
      </nav>

      {/* FULL WIDTH HERO SECTION */}
      <div className="hero-full">
        <div className="badge">
          <div className="badge-dot"></div>
          May 2026 Niche Spy Report · Free Download
        </div>

        <h1 className="hero-title sora">
          Top TPT Keywords for <span className="accent">May 2026</span><br />
          <span className="gold">— Ranked by Opportunity</span>
        </h1>

        <p className="hero-sub">
          Mother's Day is just days away. Memorial Day is coming. Plus the EOY rush starts now.
          We pulled out the top <strong style={{color: '#0f172a'}}>50 May opportunities</strong> ranked by our Golden Score formula.
          <strong>Get the full report free, direct to your inbox.</strong>
        </p>

        <div className="stats">
          <div className="stat">
            <div className="stat-num sora">50</div>
            <div className="stat-label">Keyword Opportunities</div>
          </div>
          <div className="stat">
            <div className="stat-num sora">100</div>
            <div className="stat-label">Highest Golden Score</div>
          </div>
          <div className="stat">
            <div className="stat-num sora">140+</div>
            <div className="stat-label">⭐ TPT Reviews</div>
          </div>
        </div>
      </div>

      {/* MAIN 2-COLUMN LAYOUT */}
      <div className="page-grid">

        {/* LEFT — Info Card */}
        <div className="info-card">
          <div className="section-label">What You're Getting</div>
          <h2 className="card-heading sora">Fresh data. Real opportunity.</h2>

          <p style={{fontSize: '15px', color: '#475569', lineHeight: 1.7, marginBottom: '24px'}}>
            We analysed thousands of TPT searches to find the hottest niches for May 2026. Everything is ranked by our Golden Score formula — high teacher demand divided by competing products.
          </p>

          <div className="perks-grid" style={{marginBottom: '24px'}}>
            <div className="perk"><span style={{color: '#10b981', fontSize: '16px'}}>⭐</span> Golden Score Rankings</div>
            <div className="perk"><span style={{color: '#1a56db', fontSize: '16px'}}>📈</span> Popularity Data</div>
            <div className="perk"><span style={{color: '#f59e0b', fontSize: '16px'}}>🏆</span> Competition Numbers</div>
            <div className="perk"><span style={{color: '#8b5cf6', fontSize: '16px'}}>📅</span> Seasonal Timing Tips</div>
            <div className="perk"><span style={{color: '#ec4899', fontSize: '16px'}}>💡</span> Hidden Gem Keywords</div>
            <div className="perk"><span style={{color: '#06b6d4', fontSize: '16px'}}>🎁</span> $25 Amazon Draw Entry</div>
          </div>

          <div style={{background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '12px', padding: '16px'}}>
            <div style={{fontSize: '13px', fontWeight: 700, color: '#92400e', marginBottom: '4px'}}>💡 This month's insight</div>
            <div style={{fontSize: '13px', color: '#a16207', lineHeight: 1.6}}>
              EOY shoppers have the biggest carts of the year. Teachers stocking up in May aren't just buying for the last 4 weeks — they're prepping for August. Make them bundle-friendly!
            </div>
          </div>
        </div>

        {/* RIGHT — Sticky Form Card */}
        <div className="form-card">
          <div className="section-label">Free · No Card Needed</div>
          <h2 className="card-heading sora">Get the May 2026 Report</h2>

          {/* ── SUCCESS: NEW SUBSCRIBER ── */}
          {submitState === 'success-new' && (
            <div style={{textAlign: 'center', padding: '16px 0'}}>
              <div style={{fontSize: '48px', marginBottom: '16px'}}>🎉</div>
              <div className="sora" style={{fontWeight: 800, fontSize: '20px', color: '#0f172a', marginBottom: '8px'}}>You're in!</div>
              <div style={{fontSize: '14px', color: '#64748b', lineHeight: 1.6, marginBottom: '20px'}}>
                The May 2026 keyword report is on its way to <strong style={{color: '#0f172a'}}>{email}</strong>.<br/>
                You're also entered into the monthly $25 Amazon draw. 🎁
              </div>
              <div style={{background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '12px', padding: '16px', fontSize: '13px', color: '#166534', marginBottom: '16px'}}>
                <strong>While you wait —</strong> try the full app free for 1 hour and search any keyword yourself.
              </div>
              <Link to="/app" className="submit-btn sora" style={{display: 'block', textAlign: 'center', textDecoration: 'none', lineHeight: '50px', padding: '0 16px'}}>
                Try the App Free →
              </Link>
            </div>
          )}

          {/* ── SUCCESS: EXISTING USER ── */}
          {submitState === 'success-existing' && (
            <div style={{textAlign: 'center', padding: '16px 0'}}>
              <div style={{fontSize: '48px', marginBottom: '16px'}}>👋</div>
              <div className="sora" style={{fontWeight: 800, fontSize: '20px', color: '#0f172a', marginBottom: '8px'}}>We recognise you!</div>
              <div style={{fontSize: '14px', color: '#64748b', lineHeight: 1.6, marginBottom: '20px'}}>
                Looks like <strong style={{color: '#0f172a'}}>{email}</strong> has signed up with us before — don't worry, you're all set. 📊<br/><br/>
                <strong>Check your inbox</strong> — the May 2026 keyword report is on its way right now!
              </div>
              <div style={{background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '12px', padding: '16px', fontSize: '13px', color: '#1e40af', marginBottom: '16px'}}>
                Can't find it? Check your spam folder or search for <strong>"Mr Joel"</strong> in your inbox.
              </div>
              <Link to="/app" className="submit-btn sora" style={{display: 'block', textAlign: 'center', textDecoration: 'none', lineHeight: '50px', padding: '0 16px'}}>
                Go to the App →
              </Link>
            </div>
          )}

          {/* ── FORM (idle / loading / error) ── */}
          {(submitState === 'idle' || submitState === 'loading' || submitState === 'error') && (
            <>
              <div className="form-sub">
                Get the full 50-keyword report — free, no card needed, unsubscribe any time.
              </div>

              <form onSubmit={handleFormSubmit}>
                <label className="field-label" htmlFor="report-email">Work Email Address *</label>
                <input
                  className="field-input"
                  type="email"
                  id="report-email"
                  placeholder="yourname@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={submitState === 'loading'}
                />

                <div className="gdpr-box">
                  <label className="gdpr-label">
                    <input
                      type="checkbox"
                      className="gdpr-checkbox"
                      checked={gdprChecked}
                      onChange={(e) => setGdprChecked(e.target.checked)}
                      required
                      disabled={submitState === 'loading'}
                    />
                    <span className="gdpr-text">
                      <strong>Yes, send me the May 2026 TPT keyword report.</strong><br />
                      Unsubscribe any time. We use Mailchimp. <a href="https://mailchimp.com/legal/terms" target="_blank" rel="noopener noreferrer">Privacy policy →</a>
                    </span>
                  </label>
                </div>

                {submitState === 'error' && (
                  <div style={{background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '10px', padding: '12px 14px', marginBottom: '16px', fontSize: '13px', color: '#dc2626'}}>
                    {errorMsg}
                  </div>
                )}

                <button
                  type="submit"
                  className="submit-btn sora"
                  disabled={submitState === 'loading' || !gdprChecked}
                  style={{opacity: submitState === 'loading' ? 0.7 : 1, cursor: submitState === 'loading' ? 'not-allowed' : 'pointer'}}
                >
                  {submitState === 'loading' ? 'Sending...' : 'Send Me the Report →'}
                </button>

                <div className="amazon-badge">
                  🎁 <strong>Automatic Entry:</strong> Monthly $25 Amazon voucher draw
                </div>

                <div style={{textAlign: 'center', marginTop: '12px', fontSize: '11px', color: '#94a3b8'}}>
                  🔒 No spam · Free forever
                </div>
              </form>
            </>
          )}

          {/* Testimonial inside form card */}
          {submitState === 'idle' || submitState === 'loading' || submitState === 'error' ? (
            <div className="testimonial">
              <div style={{fontSize: '13px', color: '#1e40af', fontStyle: 'italic', lineHeight: 1.6, marginBottom: '8px'}}>
                "It helped immediately with views and helped me to maximize my listings. Amazing product and superb customer service!"
              </div>
              <div style={{fontSize: '11px', color: '#3b82f6', fontWeight: 700}}>— Daysha C. &nbsp;⭐⭐⭐⭐⭐</div>
            </div>
          ) : null}

        </div>

      </div>

      {/* PREVIEW SECTION */}
      <div style={{maxWidth: '1080px', margin: '0 auto', padding: '0 24px 60px'}}>

        <div style={{background: 'white', border: '1px solid #e2e8f0', borderRadius: '24px', overflow: 'hidden', boxShadow: '0 10px 40px rgba(0,0,0,0.06)'}}>

          {/* Preview header */}
          <div style={{background: 'linear-gradient(135deg,#1e3a8a,#1a56db)', padding: '32px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '16px'}}>
            <div>
              <div style={{fontSize: '11px', fontWeight: 700, color: '#93c5fd', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '8px'}}>📋 Sneak Peek — May 2026 Report</div>
              <div className="sora" style={{fontWeight: 800, fontSize: '24px', color: 'white'}}>May's top keyword opportunities</div>
              <div style={{fontSize: '14px', color: '#bfdbfe', marginTop: '6px'}}>Sign up above to get all 50 free</div>
            </div>
            <div style={{display: 'flex', gap: '20px', flexWrap: 'wrap'}}>
              <div style={{textAlign: 'center', background: 'rgba(255,255,255,0.1)', padding: '12px 20px', borderRadius: '12px'}}>
                <div className="sora" style={{fontWeight: 900, fontSize: '24px', color: '#fbbf24'}}>★ 100</div>
                <div style={{fontSize: '11px', color: '#93c5fd', textTransform: 'uppercase', marginTop: '4px'}}>Top score</div>
              </div>
              <div style={{textAlign: 'center', background: 'rgba(255,255,255,0.1)', padding: '12px 20px', borderRadius: '12px'}}>
                <div className="sora" style={{fontWeight: 900, fontSize: '24px', color: '#4ade80'}}>50</div>
                <div style={{fontSize: '11px', color: '#93c5fd', textTransform: 'uppercase', marginTop: '4px'}}>Opportunities</div>
              </div>
            </div>
          </div>

          {/* Legend */}
          <div style={{background: '#f8fafc', padding: '16px 32px', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: '20px', flexWrap: 'wrap'}}>
            <div style={{fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em'}}>Score guide:</div>
            <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}><span className="sora" style={{background: '#f0fdf4', border: '1px solid #16a34a', color: '#15803d', fontSize: '11px', fontWeight: 700, padding: '4px 10px', borderRadius: '12px'}}>★ 70+</span><span style={{fontSize: '13px', color: '#64748b', fontWeight: 500}}>Gold Mine</span></div>
            <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}><span className="sora" style={{background: '#eff6ff', border: '1px solid #1a56db', color: '#1d4ed8', fontSize: '11px', fontWeight: 700, padding: '4px 10px', borderRadius: '12px'}}>★ 40–69</span><span style={{fontSize: '13px', color: '#64748b', fontWeight: 500}}>Strong</span></div>
            <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}><span className="sora" style={{background: 'white', border: '1px solid #e2e8f0', color: '#64748b', fontSize: '11px', fontWeight: 700, padding: '4px 10px', borderRadius: '12px'}}>★ &lt;40</span><span style={{fontSize: '13px', color: '#64748b', fontWeight: 500}}>Moderate</span></div>
          </div>

          {/* Table header */}
          <div style={{display: 'grid', gridTemplateColumns: '32px 1fr 100px 90px 110px', gap: 0, padding: '14px 32px', background: '#f1f5f9', borderBottom: '1px solid #e2e8f0'}}>
            <div style={{fontSize: '11px', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.08em'}}>#</div>
            <div style={{fontSize: '11px', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.08em'}}>Keyword</div>
            <div style={{fontSize: '11px', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.08em', textAlign: 'center'}}>Score</div>
            <div style={{fontSize: '11px', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.08em', textAlign: 'right'}}>Popularity</div>
            <div style={{fontSize: '11px', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.08em', textAlign: 'right'}}>Competition</div>
          </div>

          {/* KW rows */}
          <div>
            <div className="preview-table-row">
              <div className="sora" style={{fontSize: '13px', fontWeight: 700, color: '#cbd5e1'}}>1</div>
              <div><div style={{fontSize: '15px', fontWeight: 700, color: '#0f172a'}}>mothers day</div><div style={{fontSize: '12px', color: '#64748b', marginTop: '2px'}}>⏰ Mother's Day Sunday — last chance to publish</div></div>
              <div style={{textAlign: 'center'}}><span className="sora" style={{background: '#f0fdf4', border: '1px solid #16a34a', color: '#15803d', fontSize: '13px', fontWeight: 800, padding: '4px 12px', borderRadius: '20px', whiteSpace: 'nowrap'}}>★ 100</span></div>
              <div style={{fontSize: '14px', fontWeight: 600, color: '#475569', textAlign: 'right'}}>8,978</div>
              <div style={{fontSize: '14px', color: '#94a3b8', textAlign: 'right'}}>48,505</div>
            </div>

            <div className="preview-table-row">
              <div className="sora" style={{fontSize: '13px', fontWeight: 700, color: '#cbd5e1'}}>2</div>
              <div><div style={{fontSize: '15px', fontWeight: 700, color: '#0f172a'}}>mothers day craft</div><div style={{fontSize: '12px', color: '#64748b', marginTop: '2px'}}>Crafts always outperform in the final week</div></div>
              <div style={{textAlign: 'center'}}><span className="sora" style={{background: '#f0fdf4', border: '1px solid #16a34a', color: '#15803d', fontSize: '13px', fontWeight: 800, padding: '4px 12px', borderRadius: '20px', whiteSpace: 'nowrap'}}>★ 97</span></div>
              <div style={{fontSize: '14px', fontWeight: 600, color: '#475569', textAlign: 'right'}}>7,563</div>
              <div style={{fontSize: '14px', color: '#94a3b8', textAlign: 'right'}}>11,821</div>
            </div>

            <div className="preview-table-row">
              <div className="sora" style={{fontSize: '13px', fontWeight: 700, color: '#cbd5e1'}}>3</div>
              <div><div style={{fontSize: '15px', fontWeight: 700, color: '#0f172a'}}>assistant principal appreciation week</div><div style={{fontSize: '12px', color: '#64748b', marginTop: '2px'}}>🏅 Hidden gem — niche of niche, 298 comp</div></div>
              <div style={{textAlign: 'center'}}><span className="sora" style={{background: '#f8fafc', border: '1px solid #e2e8f0', color: '#64748b', fontSize: '13px', fontWeight: 800, padding: '4px 12px', borderRadius: '20px', whiteSpace: 'nowrap'}}>★ 31</span></div>
              <div style={{fontSize: '14px', fontWeight: 600, color: '#475569', textAlign: 'right'}}>1,478</div>
              <div style={{fontSize: '14px', color: '#94a3b8', textAlign: 'right'}}>298</div>
            </div>

            <div className="preview-table-row">
              <div className="sora" style={{fontSize: '13px', fontWeight: 700, color: '#cbd5e1'}}>4</div>
              <div><div style={{fontSize: '15px', fontWeight: 700, color: '#0f172a'}}>cinco de mayo</div><div style={{fontSize: '12px', color: '#64748b', marginTop: '2px'}}>📅 Act fast — tag existing products today</div></div>
              <div style={{textAlign: 'center'}}><span className="sora" style={{background: '#f8fafc', border: '1px solid #e2e8f0', color: '#64748b', fontSize: '13px', fontWeight: 800, padding: '4px 12px', borderRadius: '20px', whiteSpace: 'nowrap'}}>★ 30</span></div>
              <div style={{fontSize: '14px', fontWeight: 600, color: '#475569', textAlign: 'right'}}>2,418</div>
              <div style={{fontSize: '14px', color: '#94a3b8', textAlign: 'right'}}>18,627</div>
            </div>

            <div className="preview-table-row">
              <div className="sora" style={{fontSize: '13px', fontWeight: 700, color: '#cbd5e1'}}>5</div>
              <div><div style={{fontSize: '15px', fontWeight: 700, color: '#0f172a'}}>mothers day questionaire</div><div style={{fontSize: '12px', color: '#64748b', marginTop: '2px'}}>🏅 Mirror the misspelling — only 230 competitors</div></div>
              <div style={{textAlign: 'center'}}><span className="sora" style={{background: '#f8fafc', border: '1px solid #e2e8f0', color: '#64748b', fontSize: '13px', fontWeight: 800, padding: '4px 12px', borderRadius: '20px', whiteSpace: 'nowrap'}}>★ 30</span></div>
              <div style={{fontSize: '14px', fontWeight: 600, color: '#475569', textAlign: 'right'}}>1,346</div>
              <div style={{fontSize: '14px', color: '#94a3b8', textAlign: 'right'}}>230</div>
            </div>

            {/* Blur overlay rows 6-10 */}
            <div style={{position: 'relative'}}>
              <div style={{filter: 'blur(5px)', pointerEvents: 'none', opacity: 0.6}}>
                <div className="preview-table-row">
                  <div className="sora" style={{fontSize: '13px', fontWeight: 700, color: '#cbd5e1'}}>6</div>
                  <div><div style={{fontSize: '15px', fontWeight: 700, color: '#0f172a'}}>memorial day</div><div style={{fontSize: '12px', color: '#64748b'}}>🇺🇸 Top of the Memorial Day pile — start here</div></div>
                  <div style={{textAlign: 'center'}}><span className="sora" style={{background: 'white', border: '1px solid #e2e8f0', color: '#64748b', fontSize: '13px', fontWeight: 800, padding: '4px 12px', borderRadius: '20px'}}>★ 9</span></div>
                  <div style={{fontSize: '14px', fontWeight: 600, color: '#475569', textAlign: 'right'}}>733</div>
                  <div style={{fontSize: '14px', color: '#94a3b8', textAlign: 'right'}}>18,187</div>
                </div>
                <div className="preview-table-row">
                  <div className="sora" style={{fontSize: '13px', fontWeight: 700, color: '#cbd5e1'}}>7</div>
                  <div><div style={{fontSize: '15px', fontWeight: 700, color: '#0f172a'}}>end of the year activities</div><div style={{fontSize: '12px', color: '#64748b'}}>📅 EOY rush — biggest carts of the year</div></div>
                  <div style={{textAlign: 'center'}}><span className="sora" style={{background: '#eff6ff', border: '1px solid #1a56db', color: '#1d4ed8', fontSize: '13px', fontWeight: 800, padding: '4px 12px', borderRadius: '20px'}}>★ 48</span></div>
                  <div style={{fontSize: '14px', fontWeight: 600, color: '#475569', textAlign: 'right'}}>4,912</div>
                  <div style={{fontSize: '14px', color: '#94a3b8', textAlign: 'right'}}>234,651</div>
                </div>
                <div className="preview-table-row" style={{borderBottom: 'none'}}>
                  <div className="sora" style={{fontSize: '13px', fontWeight: 700, color: '#cbd5e1'}}>8</div>
                  <div><div style={{fontSize: '15px', fontWeight: 700, color: '#0f172a'}}>butterfly</div><div style={{fontSize: '12px', color: '#64748b'}}>🦋 May classroom obsession — life cycle units</div></div>
                  <div style={{textAlign: 'center'}}><span className="sora" style={{background: '#eff6ff', border: '1px solid #1a56db', color: '#1d4ed8', fontSize: '13px', fontWeight: 800, padding: '4px 12px', borderRadius: '20px'}}>★ 64</span></div>
                  <div style={{fontSize: '14px', fontWeight: 600, color: '#475569', textAlign: 'right'}}>5,886</div>
                  <div style={{fontSize: '14px', color: '#94a3b8', textAlign: 'right'}}>59,460</div>
                </div>
              </div>

              {/* Blur CTA overlay */}
              <div style={{position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(to bottom, rgba(248,250,252,0) 0%, rgba(248,250,252,0.9) 40%, rgba(248,250,252,1) 100%)'}}>
                <div style={{textAlign: 'center', padding: '30px', background: 'white', borderRadius: '20px', boxShadow: '0 10px 30px rgba(0,0,0,0.08)', border: '1px solid #e2e8f0'}}>
                  <div className="sora" style={{fontWeight: 800, fontSize: '20px', color: '#0f172a', marginBottom: '8px'}}>+ 45 more keywords in the full report</div>
                  <div style={{fontSize: '14px', color: '#64748b', marginBottom: '20px'}}>Sign up free to get all 50 in your inbox right now</div>
                  <a href="#report-email" onClick={scrollToEmail} className="sora" style={{background: 'linear-gradient(135deg,#1a56db,#1e40af)', color: 'white', fontWeight: 800, fontSize: '15px', padding: '14px 32px', borderRadius: '12px', textDecoration: 'none', display: 'inline-block', boxShadow: '0 4px 16px rgba(26,86,219,0.3)'}}>
                    Get the Full Report Free →
                  </a>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>

      {/* FOOTER */}
      <footer style={{background: 'white', borderTop: '1px solid var(--border)', textAlign: 'center', padding: '32px 24px', fontSize: '13px', color: 'var(--muted)'}}>
        <p>
          <a href="https://mrjoelstptgenerator.com" style={{color: 'var(--blue)', textDecoration: 'none'}}>mrjoelstptgenerator.com</a> &nbsp;·&nbsp;
          <Link to="/app" style={{color: 'var(--blue)', textDecoration: 'none'}}>Try the App Free</Link> &nbsp;·&nbsp;
          <a href="https://www.teacherspayteachers.com/store/mr-joels-math-science-store" style={{color: 'var(--blue)', textDecoration: 'none'}}>My TPT Store</a> &nbsp;·&nbsp;
          <a href="https://www.facebook.com/profile.php?id=61577123933005" style={{color: 'var(--blue)', textDecoration: 'none'}}>Facebook</a>
        </p>
        <p style={{marginTop: '8px'}}>© 2026 Mr Joel's Seller Tools. Not affiliated with Teachers Pay Teachers.</p>
      </footer>

    </div>
  );
};

export default May2026;